// The seven locked label colours (M4 plan decision 6). No free colour picker.
//
// Hex values come from the design-system tokens / the Private Cellar mockup's
// :root (research/design-direction/mockup-b-private-cellar.html), using the
// DARK-mode values for gold and warm white so a label reads the same in both
// site themes:
//   gold        #c9a86a  .dark --color-accent        (mockup --gold)
//   bronze      #8c6a3f  --color-bronze              (same in both themes)
//   burgundy    #5a1b22  --color-burgundy            (same in both themes)
//   forest      #2c4a38  @theme --color-forest       (the deep green; the dark
//                        theme's #5c8a6e is a lifted UI tint, not a label ink)
//   charcoal    #3a332d  @theme --color-ink-body     (the "charcoal ink"; the
//                        --color-charcoal token is a page band, not an ink)
//   warm white  #f3efe8  .dark --color-ink-display   (mockup --warm-white)
//   black       #080706  .dark --color-page          (mockup --black)
// Pure data — SSR-safe.

export type PaletteKey =
  | "gold"
  | "bronze"
  | "burgundy"
  | "forest"
  | "charcoal"
  | "warm-white"
  | "black";

export interface PaletteColour {
  key: PaletteKey;
  label: string;
  hex: string;
}

export const PALETTE: readonly PaletteColour[] = [
  { key: "gold", label: "Gold", hex: "#c9a86a" },
  { key: "bronze", label: "Bronze", hex: "#8c6a3f" },
  { key: "burgundy", label: "Burgundy", hex: "#5a1b22" },
  { key: "forest", label: "Forest green", hex: "#2c4a38" },
  { key: "charcoal", label: "Charcoal", hex: "#3a332d" },
  { key: "warm-white", label: "Warm white", hex: "#f3efe8" },
  { key: "black", label: "Black", hex: "#080706" },
] as const;

export const PALETTE_HEX: Readonly<Record<PaletteKey, string>> = Object.fromEntries(
  PALETTE.map((c) => [c.key, c.hex]),
) as Record<PaletteKey, string>;

/** The palette entry for a hex (case-insensitive), or null for an off-palette value. */
export function paletteColour(hex: string | null | undefined): PaletteColour | null {
  if (!hex) return null;
  const h = hex.toLowerCase();
  return PALETTE.find((c) => c.hex === h) ?? null;
}

/** Relative luminance (0–1) — used to pick legible selection chrome over a colour. */
export function luminance(hex: string): number {
  const m = /^#?([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i.exec(hex);
  if (!m) return 0;
  const lin = (s: string) => {
    const c = parseInt(s, 16) / 255;
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * lin(m[1]) + 0.7152 * lin(m[2]) + 0.0722 * lin(m[3]);
}
